import React from 'react';
import AtlasV2DorisDoubleDropdownChart from 'src/components/AtlasV2Components/AtlasV2graphs/AtlasV2DorisDoubleDropdownChart';
import { OTHER_TRAFFIC_FILED_IDS } from 'src/components/AtlasV2Components/AtlasV2TrafficPageComponents/AtlasV2TotalTraffic/AtlasV2TotalTrafficTopEntity';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';
import { useComparisonTimePeriodShortDisplayName, useMainTimePeriodShortDisplayName } from 'src/utils/Hooks/typedHooks';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import { CHART_NAMES } from 'src/utils/chartContext/chartContextRegistry';

export interface TrafficField {
  fieldName: string;
  /** Display label for the field */
  label: string;

  metricType: string;

  metricTypeDisplay: METRICTYPE;
  /** Whether this field represents a market share value */
  isMarketShare?: boolean;

  marketShareCalculator?: (value: number) => number;
}

export interface GroupByField {
  id: string;

  label: string;

  indexName?: string;
}

interface AtlasV2TrafficDropdownTopEntityTrafficProps {
  /** Unique key for the query */
  queryKey: string;
  /** Array of fields to group by */
  groupByFields: GroupByField[];
  /** Array of traffic metric fields to display */
  fields?: TrafficField[];
}

const AtlasV2TrafficDropdownTopEntityTraffic = ({
  queryKey,
  groupByFields,
  fields = [
    {
      fieldName: 'totalClicksShare',
      label: 'Share of Total Traffic',
      metricType: 'totalClicks',
      metricTypeDisplay: METRICTYPE.PERCENT,
      isMarketShare: true
    },
    {
      fieldName: 'totalClicks',
      label: 'Total Traffic',
      metricType: 'totalClicks',
      metricTypeDisplay: METRICTYPE.VOLUME
    }
  ]
}: AtlasV2TrafficDropdownTopEntityTrafficProps) => {
  const entityPhrase = useEntityPhrasing();
  const mainTimePeriodShortDisplayName = useMainTimePeriodShortDisplayName();
  const comparisonTimeDisplayName = useComparisonTimePeriodShortDisplayName();
  const taxonomyText = useTaxonomySubtitleText();

  return (
    <AtlasV2DorisDoubleDropdownChart
      groupByFields={groupByFields}
      fields={fields}
      queryKey={queryKey}
      endPoint="GetTrafficByType"
      showSingleGroupBy
      chartContextId={CHART_NAMES.TRAFFIC_BY_KEYWORD_COLUMN}
      preProcessData={(data) => {
        const otherTraffics = data.filter((datum) => OTHER_TRAFFIC_FILED_IDS.includes(datum.fieldId));
        const remainingData = data.filter((datum) => !OTHER_TRAFFIC_FILED_IDS.includes(datum.fieldId));
        return [...remainingData, ...otherTraffics];
      }}
      beaconChartWithLegendPropsBuilder={(props) => {
        return {
          ...props,
          includeSummaryInfo: false,
          loadingComponent: <SplineChartLoading includeSubtitle />,
          subtitle: (
            <BeaconSubtitleContainer>
              <Text variant="body1" lineHeight="24px">
                {`The chart displays the ${props.selectedField.label.toLocaleLowerCase()} for ${entityPhrase} in each
                ${props.selectedGroupByField.toLocaleLowerCase()} ${taxonomyText} during the ${mainTimePeriodShortDisplayName} period
                compared to the ${comparisonTimeDisplayName}.`}
                <SubtitleFilters showFilterNumber={false} />
              </Text>
            </BeaconSubtitleContainer>
          )
        };
      }}
    />
  );
};
export default AtlasV2TrafficDropdownTopEntityTraffic;
